import {Check} from '@sparkware/app-utils';
import {IServiceFactoryExtended} from '../../../service-factory-extended.interface';
import {PausableTimer} from '../../../../../common/utils/pausable-timer';
import {action, makeObservable, observable} from 'mobx';
import {IBitrateStats, IBufferingTime, IFramesDropStats, ILogType, IVideoPlayer} from '../video-player.interface';
import { OrbitMarketingEventAnalyticsLabel } from "../../../analytics/analytics.service.interface";

const DROP_FRAMES_THRESHOLD: number = 0.2;

export class VideoLiveStreamBasePlayer implements IVideoPlayer {

    private _gameType: number;
    private _videoReference: HTMLVideoElement;
    private _streamSource: string;
    private _playerType: string;
    private _pausableTimer: PausableTimer = null;

    private _bufferingStartTime: number;
    private _firstLoadTimeLogged: boolean = false;
    private _isFirstTimePlay: boolean = true;
    private _bufferingTimeValues: number[] = [];
    private _hadPlayStarted: boolean = false;
    private _isVisible: boolean = false;
    private _watchTimeLimitMs: number = 0;

    @observable.ref
    hasError: boolean = false;

    @action.bound
    _setHasError(value:boolean): void {
        this.hasError = value;
    }

    @observable.ref
    isLoading: boolean = true;

    @action.bound
    _setIsLoading(value:boolean): void {
        this.isLoading = value;
    }

    @observable.ref
    watchTimeLimitReached: boolean = false;

    @action.bound
    _setWatchTimeLimitReached(value:boolean): void {
        this.watchTimeLimitReached = value;
    }

    @observable.ref
    autoplayAllowed: boolean = true;

    @action.bound
    _setAutoplayAllowed(value:boolean): void {
        this.autoplayAllowed = value;
    }

    private _getGACategory = (): string => {
        return this._services.liveStreamPlaying.gaCategory;
    }

    constructor(playerType:string, gameType:number, videoReference: HTMLVideoElement, streamSource:string, watchTimeLimit: number, private readonly _services: IServiceFactoryExtended) {
        makeObservable(this);

        this._playerType = playerType;
        this._videoReference = videoReference;
        this._streamSource = streamSource;
        this._gameType = gameType;
        this._watchTimeLimitMs = watchTimeLimit;

        this._pausableTimer = new PausableTimer(() => {
            this.onWatchTimeLimitReached();
        }, watchTimeLimit);

        window.addEventListener('offline', this._onGoingOffline);
        this._videoReference.addEventListener('waiting', this._onWaiting);
        this._videoReference.addEventListener('playing', this._onPlaying);
        this._videoReference.addEventListener('canplay', this._onCanPlay);
        this._videoReference.addEventListener('error', this._onError);
    }

    get videoReference(): HTMLVideoElement {
        return this._videoReference;
    }

    get streamSource(): string {
        return this._streamSource;
    }

    play() {
        if (!this.watchTimeLimitReached) {
            this._isVisible = true;
            this._bufferingStartTime = performance.now();
            console.log('LSP play video for ' + this._gameType);
            this._videoReference.play()
                .then(() => {
                    this._setAutoplayAllowed(true);
                    console.log('LSP set auto-play video for ' + this._gameType);
                })
                .catch(error => {
                    if (error.name === 'NotAllowedError') {
                        // error triggered when autoplay of video is not allowed
                        this._onAutoplayError();
                    } else {
                        this._onError(error);
                    }
                });

            if (this._isFirstTimePlay) {
                this._isFirstTimePlay = false;
                this._log('info', 'play');
                this._services.analytics.sendOrbitMarketingEvent(this._getGACategory(), 'IN_VIEW', OrbitMarketingEventAnalyticsLabel.VideoPlayback, this._gameType.toString());
            }
        }
    }

    pause() {
        this._isVisible = false;

        console.log('LSP try pause for ' + this._gameType);

        // we can pause the video only if had ever started playing. A pause before first time play will cause in an error
        // play() request was interrupted by a call to pause()
        if (this._hadPlayStarted) {
            this._videoReference.pause();
            console.log('LSP pause for ' + this._gameType);
        }

        this._pausableTimer.pause();
        this._logAverageBufferingTimeOnPause();
    }

    load() {
        this._videoReference.load();
    }

    handleError(error: any) {
        this._onError(error);
    }

    onWatchTimeLimitReached() {
        this._videoReference.pause();
        this._setWatchTimeLimitReached(true);
        this._logAverageBufferingTimeOnEnd();
        this._services.analytics.sendOrbitMarketingEvent(this._getGACategory(), 'LIMIT_REACH', `VIDEO_PLAYBACK_LIMIT_${this._watchTimeLimitMs / 1000}`, this._gameType.toString());
    }

    destroy() {
        this._pausableTimer.destroy();
        this._pausableTimer = null;

        window.removeEventListener('offline', this._onGoingOffline);
        this._videoReference.removeEventListener('waiting', this._onWaiting);
        this._videoReference.removeEventListener('playing', this._onPlaying);
        this._videoReference.removeEventListener('canplay', this._onCanPlay);
        this._videoReference.removeEventListener('error', this._onError);
        this._videoReference = null;
    }

    logUIAction(type: ILogType, action: string) {
        this._log(type, action);
    }

    protected _onWaiting = () => {
        // Occurs when playback stops because the next frame of a video resource is not available.
        // Example: video had played everything that was loaded and cannot play further until the next sequence is loaded
        if (this.autoplayAllowed) {
            // For some reason, the waiting event is dispatched after catching the 'NotAllowedError'
            // The following code related to loading should execute only when we have a real waiting to load,
            // so only when the playback can start
            this._bufferingStartTime = performance.now();
            this._setIsLoading(true);
        }
        this._pausableTimer.pause();
    }

    protected _onPlaying = () => {
        // Occurs when the video playback is first started or when the playback resumes.
        this._hadPlayStarted = true;
        this._logFirstLoadTimeUntilPlay();
        this._setIsLoading(false);
        this._pausableTimer.resume();

        console.log('LSP onPlaying event for ' + this._gameType);
        
        if (!this._isVisible) {
            console.log('LSP onPlaying but paused due to not visible for ' + this._gameType);
            this.pause();
        }
    }

    private _onCanPlay = () => {
        // Occurs when the video playback resumes.
        // Example: if video was previously paused due to a waiting event, this will let us know that the next sequence is loaded
        // and the video will play further on
        // NOTE: usually the 'playing' event is triggered in the same conditions, but on iOS devices is not so we cannot base on 'playing' event
        // Example: on iOS devices, even if the video is playing, if you press unmute for the first time, a waiting event will be triggered, without
        // having also a 'playing' event trigger. Related bug: #1755199
        this._bufferingTimeValues.push(performance.now() - this._bufferingStartTime);
        this._setIsLoading(false);
        this._pausableTimer.resume();
        console.log('LSP onCanPlay event for ' + this._gameType);
    }

    private _onError = (err: ErrorEvent) => {
        this._setHasError(true);
        console.error('Live stream error', err);
        this._log('error', 'VideoStreamError');
    }

    private _onAutoplayError = () => {
        console.error('LSP AutoplayNotAllowed error for ' + this._gameType);

        this._setAutoplayAllowed(false);
        this._setIsLoading(false);
        this._log('info', 'AutoplayNotAllowed');
        this._services.analytics.sendOrbitMarketingEvent(this._getGACategory(), 'AUTOPLAY', OrbitMarketingEventAnalyticsLabel.Error, this._gameType.toString());
    }

    private _onGoingOffline = () => {
        this._setHasError(true);
    }

    private _getBufferAverageTime(): number {
        return this._bufferingTimeValues.reduce((prevValue, currentValue) => {
            return prevValue + currentValue
        }, 0);
    }

    private _logAverageBufferingTimeOnPause() {
        const totalBufferTime = this._getBufferAverageTime();
        const customData: IBufferingTime = {
            time: totalBufferTime / this._bufferingTimeValues.length
        };

        this._log('info', 'AvgBufferingTime', customData);
    }

    private _logAverageBufferingTimeOnEnd() {
        const totalBufferTime = this._getBufferAverageTime();
        const customData: IBufferingTime = {
            time: totalBufferTime / this._bufferingTimeValues.length,
            occurrence: this._bufferingTimeValues.length
        };

        this._log('info', 'AvgBufferingData', customData);
    }

    private _logFirstLoadTimeUntilPlay() {
        if (!this._firstLoadTimeLogged) {
            const firstLoadTime = performance.now() - this._bufferingStartTime;
            const loadTime: IBufferingTime = {
                time: firstLoadTime
            }
            this._firstLoadTimeLogged = true;
            this._log('info', 'FirstLoad', loadTime);
            this._services.analytics.sendOrbitMarketingEvent(this._getGACategory(), 'PLAY', OrbitMarketingEventAnalyticsLabel.VideoPlayback, this._gameType.toString());
        }
    }

    protected _checkDroppedFPS(droppedFrames: number, decodedFrames: number) {
        if (!this._videoReference.paused) {
            // when video is paused, we do not care about video frames
            // since on paused no frame is rendered

            const droppedFramesRatio = droppedFrames/decodedFrames;
            if (droppedFramesRatio > DROP_FRAMES_THRESHOLD) {
                // when dropped frames ratio is greater than the threshold we control
                // we want to log it to Elastic

                const framesDropData: IFramesDropStats = {
                    droppedFrames: droppedFrames,
                    totalFrames: decodedFrames
                }
                this._log('info', 'FpsDrop', framesDropData);
            }
        }
    }

    protected _log(type: ILogType, action: string, customData: IBufferingTime | IBitrateStats | IFramesDropStats = null) {
        let structuredLoger = this._services.sdk.structuredLoggingService
                                .getLogger()
                                .addCategory(this._services.liveStreamPlaying.gaCategory)
                                .addAction(action)
                                .addLabel(this._playerType)
                                .addValue(this._gameType.toString());

        if (!Check.isNullOrUndefined(customData)) {
            structuredLoger.addCustomData(customData);
        }

        switch (type) {
            case 'error':
                structuredLoger.error();
            break;
            case 'info':
            default:
                structuredLoger.info();
            break;
        }
    }
}
